import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { intelligentSearch } from '../utils/searchUtils';

const DEFAULT_OFFICES = [
  'CONSULTORIOS EXTERNOS', 'ECOGRAFIAS .', 'FONOAUDIOLOGA .',
  'NEUMONOLOGO .', 'NEUROLOGO .', 'OTORRINO .', 'PSICOLOGIA .', 'RAYOS X'
];

const getLocalYYYYMMDD = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const useWaitingRoom = (staffName) => {
  const { addNotification } = useNotifications();

  const [viewDate, setViewDate] = useState(() => getLocalYYYYMMDD());
  const [isReduced, setIsReduced] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(staffName || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmitModalOpen, setIsAdmitModalOpen] = useState(false);
  const [admitData, setAdmitData] = useState({ patient_name: '', patient_dni: '', professional_name: staffName || '' });
  const [settings, setSettings] = useState({ playSound: false, useCaller: true });
  const [sortBy, setSortBy] = useState('time');
  const [consultorios, setConsultorios] = useState([]);
  const [attendedPatient, setAttendedPatient] = useState(null);


  const playArrivalChime = () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();
      const now = ctx.currentTime;
      const playTone = (freq, start, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.1, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
        osc.start(start);
        osc.stop(start + duration);
      };
      playTone(1046.50, now, 0.3); // C6
      playTone(1318.51, now + 0.15, 0.3); // E6
      playTone(1567.98, now + 0.3, 0.4); // G6
    } catch (err) {
      console.error("Failed to play arrival chime:", err);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*').order('full_name', { ascending: true });
      if (error) throw error;
      if (data && data.length > 0) {
        setProfessionals(data.map(p => ({ id: p.id, name: p.full_name || p.name, specialty: p.specialty })));
      } else {
        setProfessionals(DEFAULT_OFFICES.map((name, id) => ({ id, name })));
      }
    } catch (err) {
      console.error("Error fetching professionals for WR:", err);
      setProfessionals(DEFAULT_OFFICES.map((name, id) => ({ id, name })));
    }

    try {
      const { data: roomsData, error: roomsError } = await supabase.from('consultorios').select('*').order('name', { ascending: true });
      if (roomsError) throw roomsError;
      if (roomsData) setConsultorios(roomsData);
    } catch (err) {
      console.error("Error fetching consultorios for WR:", err);
    }
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('appointments').select('*').eq('appointment_date', viewDate).order('appointment_time', { ascending: true });
      if (error) throw error;
      setPatients(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    await fetchPatients();
    addNotification('Datos actualizados', 'info');
  };

  useEffect(() => {
    Promise.resolve().then(() => fetchConfig());
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => fetchPatients());
  }, [viewDate]);

  const filteredPatients = useMemo(() => {
    let result = [...patients];
    if (selectedProfessional && selectedProfessional !== 'TODOS' && selectedProfessional !== 'ADMINISTRACION GDT') {
      result = result.filter(p => p.professional_name === selectedProfessional);
    }
    if (searchTerm) {
      result = result.filter(p => intelligentSearch(searchTerm, p.patient_name, p.patient_dni));
    }
    
    // Deduplicate any potential duplicate records in state
    const seenKeys = new Set();
    result = result.filter(p => {
      const normName = (p.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim().toUpperCase();
      const normDni = (p.patient_dni || '').replace(/[^0-9]/g, '').trim();
      const timePart = (p.appointment_time || '').substring(0, 5);
      const key = `${p.appointment_date}_${timePart}_${(p.professional_name || '').trim()}_${normDni || normName}`;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });

    // Sort logic
    if (sortBy === 'arrival') {
      result.sort((a, b) => {
        if (a.arrival_time && !b.arrival_time) return -1;
        if (!a.arrival_time && b.arrival_time) return 1;
        if (a.arrival_time && b.arrival_time) return new Date(a.arrival_time) - new Date(b.arrival_time);
        return (a.appointment_time || '').localeCompare(b.appointment_time || '');
      });
    } else {
      result.sort((a, b) => (a.appointment_time || '').localeCompare(b.appointment_time || ''));
    }
    
    return result;
  }, [patients, selectedProfessional, searchTerm, sortBy]);

  const updateStatus = async (id, newStatus, name) => {
    try {
      const currentPatient = patients.find(p => p.id === id);

      if (newStatus === 'en-llamado' || newStatus === 'en-consultorio') {
        const patientDni = currentPatient?.patient_dni || currentPatient?.study_details?.patient_dni;
        const patientName = name || currentPatient?.patient_name;

        // Check if patient has another active appointment today currently in call or room
        const { data: busyData } = await supabase.from('appointments')
          .select('id, patient_name, patient_dni, professional_name, consultorio_name, status, appointment_time')
          .eq('appointment_date', viewDate)
          .neq('id', id)
          .in('status', ['en-llamado', 'en-consultorio', 'atendido']);

        if (busyData && busyData.length > 0) {
          const normDni = (patientDni || '').replace(/[^0-9]/g, '').trim();
          const normName = (patientName || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();

          const conflict = busyData.find(other => {
            const otherDni = (other.patient_dni || '').replace(/[^0-9]/g, '').trim();
            const otherName = (other.patient_name || '').replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();
            if (normDni && otherDni && normDni === otherDni) return true;
            if (normName && otherName && normName === otherName) return true;
            return false;
          });

          if (conflict) {
            const statusLabel = conflict.status === 'en-consultorio' ? 'siendo atendido' : (conflict.status === 'atendido' ? 'marcado como Atendido' : 'en llamado');
            const roomLabel = conflict.consultorio_name || conflict.professional_name || 'otra área';
            const confirmCall = window.confirm(
              `⚠️ ¡ATENCIÓN! PACIENTE EN ATENCIÓN EN OTRA SALA\n\n` +
              `El paciente "${patientName}" figura actualmente ${statusLabel} en ${roomLabel}.\n\n` +
              `¿Deseas llamarlo de todas formas a esta consulta?`
            );
            if (!confirmCall) return;
          }
        }
      }

      const updateData = { status: newStatus, updated_at: new Date().toISOString() };
      
      if (newStatus === 'presente' || newStatus === 'en-llamado') {
        if (currentPatient && !currentPatient.arrival_time) updateData.arrival_time = new Date().toISOString();
      } else if (newStatus === 'atendido') {
        updateData.attended_time = new Date().toISOString();
      } else if (newStatus === 'pendiente') {
        updateData.arrival_time = null;
        updateData.attended_time = null;
      }

      const { error } = await supabase.from('appointments').update(updateData).eq('id', id);
      if (error) throw error;
      addNotification(`${name} ahora está ${newStatus.toUpperCase()}`, 'success');
      
      if (newStatus === 'presente' && settings.playSound) playArrivalChime();
      fetchPatients();
    } catch (err) {
      console.error("Error updating status:", err);
      addNotification('Error al actualizar estado', 'error');
    }
  };

  const handleAssignConsultory = async (id, roomName) => {
    try {
      const { error } = await supabase.from('appointments').update({ consultorio_name: roomName || null }).eq('id', id);
      if (error) throw error;
      addNotification('Consultorio asignado', 'success');
      setPatients(prev => prev.map(p => p.id === id ? { ...p, consultorio_name: roomName } : p));
    } catch (err) {
      console.error("Error assigning consultory:", err);
      addNotification('Error al asignar consultorio', 'error');
    }
  };

  const handleAttendPatient = async (p) => {
    await updateStatus(p.id, 'atendido', p.patient_name);
    setAttendedPatient(p);
  };

  const handleCall = async (p) => {
    addNotification(`Llamando a ${p.patient_name}...`, 'info');
    updateStatus(p.id, 'en-llamado', p.patient_name);
  };

  const handleQuickAdmit = async () => {
    if (!admitData.patient_name || !admitData.professional_name) {
      addNotification('Por favor, completa nombre y profesional.', 'warning');
      return;
    }
    
    try {
      const nowStr = new Date().toISOString();
      const isEditing = !!admitData.id;
      
      if (isEditing) {
        const { error } = await supabase.from('appointments').update({
          patient_name: admitData.patient_name,
          patient_dni: admitData.patient_dni,
          professional_name: admitData.professional_name
        }).eq('id', admitData.id);
        if (error) throw error;
        addNotification('Admisión actualizada correctamente.', 'success');
      } else {
        const { error } = await supabase.from('appointments').insert([{
          patient_name: admitData.patient_name,
          patient_dni: admitData.patient_dni,
          patient_email: '',
          patient_phone: '',
          professional_name: admitData.professional_name,
          appointment_date: getLocalYYYYMMDD(),
          appointment_time: new Date().toLocaleTimeString('es-AR', { hour12: false }),
          status: 'presente',
          arrival_time: nowStr,
          study_type: 'ESPONTÁNEA',
          created_by_user: (() => { const u = JSON.parse(localStorage.getItem('gdt_active_user') || '{}'); const s = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}'); return u.display_name || u.username || s.full_name || 'Sistema'; })()
        }]);
        if (error) throw error;
        addNotification('Paciente admitido correctamente.', 'success');
        if (settings.playSound) playArrivalChime();
      }
      
      setIsAdmitModalOpen(false);
      setAdmitData({ patient_name: '', patient_dni: '', professional_name: staffName || '' });
      fetchPatients();
    } catch (err) {
      console.error("Error admitting patient:", err);
      addNotification('Error al admitir paciente.', 'error');
    }
  };

  const deleteAppointment = async (id) => {
    if(window.confirm('¿Eliminar?')) {
      await supabase.from('appointments').delete().eq('id', id); 
      fetchPatients();
    }
  };

  return {
    sortBy, setSortBy,
    viewDate, setViewDate,
    isReduced, setIsReduced,
    patients, setPatients,
    loading, setLoading,
    professionals, setProfessionals,
    selectedProfessional, setSelectedProfessional,
    searchTerm, setSearchTerm,
    isAdmitModalOpen, setIsAdmitModalOpen,
    admitData, setAdmitData,
    settings, setSettings,
    consultorios, setConsultorios,
    attendedPatient, setAttendedPatient,

    filteredPatients,
    handleManualRefresh,
    updateStatus,
    handleAssignConsultory,
    handleAttendPatient,
    handleCall,
    handleQuickAdmit,
    deleteAppointment
  };
};
